import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import Calendar from "./Calendar";
import PdfDownloadAlert from "../orders/PdfDownloadAlert";
import Notification from "../utility/Notification";
import { handleDownload } from "../orders/PDFDialog";
import { FaChevronDown, FaCalendarDay, FaClipboardList, FaEye, FaEyeSlash, FaFilePdf } from "react-icons/fa";
import {
  getOrdersForMonth,
  getClientsData,
  getClientEarnings,
  getClientDailyEarnings,
  getAllClientDebts,
} from "../utility/EndPointGoogleFirebase";

const fmt = (n) => `${parseFloat(n || 0).toFixed(2)} €`;
const blurStyle = (show) => ({
  filter: show ? "none" : "blur(6px)",
  userSelect: show ? "auto" : "none",
  transition: "filter 0.2s",
  pointerEvents: show ? "auto" : "none",
});

// ── Accordion stat card ──────────────────────────────────────────────
const StatCard = ({ accentColor, icon, label, bigNumber, bigNumberColor, sub, children }) => {
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);

  const handleEye = (e) => {
    e.stopPropagation();
    setShow(v => !v);
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 14,
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      overflow: "hidden", borderTop: `3px solid ${accentColor}`,
    }}>
      <div
        onClick={() => setOpen(v => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 16px", cursor: "pointer", userSelect: "none",
          WebkitTapHighlightColor: "transparent",
          borderBottom: open ? "1px solid #f3f4f6" : "none",
        }}
      >
        <div style={{
          width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
          background: `${accentColor}18`, border: `1.5px solid ${accentColor}44`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accentColor, fontSize: 16,
        }}>
          {icon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase" }}>
            {label}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color: bigNumberColor, lineHeight: 1.2, marginTop: 2, ...blurStyle(show) }}>
            {bigNumber}
          </div>
          {sub && (
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1, textTransform: "capitalize" }}>{sub}</div>
          )}
        </div>

        <div
          onClick={handleEye}
          style={{
            width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: show ? accentColor : "#d1d5db",
            background: show ? `${accentColor}12` : "#f9fafb",
            border: `1px solid ${show ? accentColor + "44" : "#e5e7eb"}`,
            cursor: "pointer", transition: "all 0.2s", fontSize: 13,
          }}
        >
          {show ? <FaEye /> : <FaEyeSlash />}
        </div>

        <div style={{
          color: "#9ca3af", fontSize: 12, flexShrink: 0,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.25s ease", pointerEvents: "none",
        }}>
          <FaChevronDown />
        </div>
      </div>

      {open && (
        <div style={{ padding: "14px 16px" }}>
          {typeof children === "function" ? children(show) : children}
        </div>
      )}
    </div>
  );
};

// ── Client accordion block con bottone PDF ───────────────────────────
const ClientBlock = ({ client, totals, debt, isLast, show, onDownloadPdf }) => {
  const [open, setOpen] = useState(false);

  const ordersVal    = parseFloat(totals.totalOrders       || 0);
  const accontoVal   = parseFloat(totals.totalDownPayments || 0);
  const diffVal      = parseFloat(totals.totalDifference   || 0);
  const debtVal      = parseFloat(debt || 0);
  const totaleDovuto = diffVal + debtVal;
  const isPositive   = totaleDovuto >= 0;

  const Row = ({ label, value, valueColor }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13, paddingBottom: 5 }}>
      <span style={{ color: "#6b7280" }}>{label}</span>
      <span style={{ fontWeight: 500, color: show ? (valueColor || "#374151") : "#9ca3af", ...blurStyle(show) }}>
        {value}
      </span>
    </div>
  );

  const handlePdfClick = (e) => {
    e.stopPropagation();
    onDownloadPdf(client);
  };

  return (
    <div>
      {/* Accordion header */}
      <div
        onClick={() => setOpen(v => !v)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 0", cursor: "pointer", userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <span style={{
          fontWeight: 700, fontSize: 13, color: "#111827",
          textTransform: "uppercase", letterSpacing: "0.05em",
          flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {client}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{
            fontSize: 12, fontWeight: 700,
            color: isPositive ? "#15803d" : "#dc2626",
            background: isPositive ? "#f0fdf4" : "#fff1f2",
            border: `1px solid ${isPositive ? "#bbf7d0" : "#fecaca"}`,
            borderRadius: 20, padding: "2px 10px",
            ...blurStyle(show),
          }}>
            {fmt(totaleDovuto)}
          </span>
          <div style={{
            color: "#9ca3af", fontSize: 11,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}>
            <FaChevronDown />
          </div>
        </div>
      </div>

      {/* Dettaglio espandibile */}
      {open && (
        <div style={{ padding: "10px 12px 12px", background: "#f9fafb", borderRadius: 10, marginBottom: 4 }}>
          <Row label="Ordini"           value={fmt(ordersVal)}                          valueColor="#374151" />
          <Row label="Acconto"          value={accontoVal > 0 ? fmt(accontoVal) : "—"}  valueColor="#2563eb" />
          <Row label="Totale Mese"      value={fmt(diffVal)}                            valueColor={diffVal >= 0 ? "#15803d" : "#dc2626"} />
          <Row label="Saldo Mese Prec." value={debtVal > 0 ? fmt(debtVal) : "—"}       valueColor="#dc2626" />

          {/* Totale dovuto */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginTop: 8, padding: "7px 10px", borderRadius: 8,
            background: isPositive ? "#f0fdf4" : "#fff1f2",
            border: `1px solid ${isPositive ? "#bbf7d0" : "#fecaca"}`,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Totale dovuto</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: isPositive ? "#15803d" : "#dc2626", ...blurStyle(show) }}>
              {fmt(totaleDovuto)}
            </span>
          </div>

          {/* Bottone PDF */}
          <button
            onClick={handlePdfClick}
            style={{
              marginTop: 10, width: "100%",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "8px 12px", borderRadius: 8, border: "none",
              background: "linear-gradient(135deg, #be123c, #e11d48)",
              color: "#fff", fontSize: 12, fontWeight: 500,
              cursor: "pointer", fontFamily: "inherit",
              WebkitTapHighlightColor: "transparent",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            <FaFilePdf style={{ fontSize: 13 }} />
            Scarica PDF
          </button>
        </div>
      )}

      {!isLast && <div style={{ borderBottom: "1px dashed #e5e7eb", margin: "2px 0" }} />}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────
const Statistics = () => {
  const today = dayjs().format("YYYY-MM-DD");
  const [selectedDate, setSelectedDate]           = useState(today);
  const [orders, setOrders]                       = useState({});
  const [clientTotals, setClientTotals]           = useState({});
  const [dailyClientTotals, setDailyClientTotals] = useState({});
  const [totalEarnings, setTotalEarnings]         = useState(0);
  const [dayEarnings, setDayEarnings]             = useState(0);
  const [isLoading, setIsLoading]                 = useState(false);
  const [clientDebts, setClientDebts]             = useState({});

  // PDF state
  const [isPdfAlertVisible, setIsPdfAlertVisible] = useState(false);
  const [selectedClientForPdf, setSelectedClientForPdf] = useState(null);
  const [notification, setNotification]           = useState(null);

  const currentMonthName = dayjs(selectedDate).format("MMMM YYYY");

  const fetchDailyClientTotals = async (clients, day, month, year) => {
    const totals = {};
    await Promise.all(clients.map(async (client) => {
      totals[client] = parseFloat(await getClientDailyEarnings(client, month, year, day) || 0);
    }));
    return totals;
  };

  const fetchMonthlyClientTotals = async (clients, month, year) => {
    const totals = {};
    await Promise.all(clients.map(async (client) => {
      totals[client] = await getClientEarnings(client, month, year);
    }));
    return totals;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const date = dayjs(selectedDate);
      const day = date.date(), month = date.month() + 1, year = date.year();
      try {
        const [allOrders, clientsData, debtsData] = await Promise.all([
          getOrdersForMonth(month, year),
          getClientsData(),
          getAllClientDebts(),
        ]);
        setClientDebts(debtsData);
        const ordersMap = allOrders.reduce((acc, o) => {
          const key = `${o.anno}-${String(o.mese).padStart(2,"0")}-${String(o.giorno).padStart(2,"0")}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(o);
          return acc;
        }, {});
        setOrders(ordersMap);
        if (clientsData.length > 0) {
          const daily = await fetchDailyClientTotals(clientsData, day, month, year);
          setDailyClientTotals(daily);
          setDayEarnings(Object.values(daily).reduce((a, v) => a + v, 0));
          const monthly = await fetchMonthlyClientTotals(clientsData, month, year);
          setClientTotals(monthly);
          setTotalEarnings(Object.values(monthly).reduce((a, v) => a + parseFloat(v.totalOrders || 0), 0));
        } else {
          setDailyClientTotals({}); setClientTotals({});
          setDayEarnings(0); setTotalEarnings(0);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedDate]);

  // PDF handlers
  const handleDownloadPdf = (clientName) => {
    setSelectedClientForPdf(clientName);
    setIsPdfAlertVisible(true);
  };

  const confirmDownloadPdf = async () => {
    try {
      setIsLoading(true);
      await handleDownload(selectedClientForPdf, selectedDate);
      setNotification({ type: "success", message: `PDF scaricato per ${selectedClientForPdf}.` });
    } catch (error) {
      setNotification({ type: "error", message: `Errore download PDF per ${selectedClientForPdf}.` });
    } finally {
      setIsLoading(false);
      setIsPdfAlertVisible(false);
    }
  };

  const grandTotalDovuto = Object.entries(clientTotals).reduce((sum, [client, totals]) => {
    const diff = parseFloat(totals.totalDifference || 0);
    const debt = parseFloat(clientDebts[client] || 0);
    return sum + diff + debt;
  }, 0);

  const clientEntries = Object.entries(clientTotals);

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        .st-loader-ring {
          width: 36px; height: 36px;
          border: 2px solid #e5e7eb; border-top-color: #4f46e5;
          border-radius: 50%; animation: stSpin 0.8s linear infinite;
        }
        @keyframes stSpin { to { transform: rotate(360deg); } }
        .st-wrapper {
          max-width: 1100px; margin: 0 auto; padding: 16px 12px;
          padding-left: max(12px, env(safe-area-inset-left));
          padding-right: max(12px, env(safe-area-inset-right));
          padding-bottom: max(32px, env(safe-area-inset-bottom));
          display: flex; flex-direction: column; gap: 16px;
        }
        @media (min-width: 480px)  { .st-wrapper { padding: 20px 16px; } }
        @media (min-width: 768px)  { .st-wrapper { padding: 28px 24px; } }
        @media (min-width: 1024px) { .st-wrapper { padding: 36px 32px; } }
        .page-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(22px, 5vw, 34px);
          font-weight: 400; color: #111827; line-height: 1.2;
        }
        .st-grid {
          display: grid; grid-template-columns: 1fr; gap: 14px;
        }
        @media (min-width: 700px) { .st-grid { grid-template-columns: repeat(2, 1fr); } }
        .st-client-row {
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 8px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; font-size: 13px;
        }
        .st-client-row:last-child { border-bottom: none; }
        .st-section-label {
          font-size: 11px; font-weight: 500; color: #9ca3af;
          letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 10px;
        }
      `}</style>

      {/* Loading overlay */}
      {isLoading && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14,
          background: "rgba(249,250,251,0.88)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        }}>
          <div className="st-loader-ring" />
          <p style={{ fontSize: 12, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase" }}>Attendere</p>
        </div>
      )}

      {/* Notification + PDF modal */}
      <Notification notification={notification} onClose={() => setNotification(null)} />
      <PdfDownloadAlert
        isVisible={isPdfAlertVisible}
        clientName={selectedClientForPdf}
        onCancel={() => setIsPdfAlertVisible(false)}
        onConfirm={confirmDownloadPdf}
      />

      <div className="st-wrapper">

        {/* Page header */}
        <div style={{ textAlign: "center", paddingBottom: 20, borderBottom: "1px solid #e5e7eb" }}>
          <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 6 }}>Riepilogo</p>
          <h1 className="page-title">Statistiche</h1>
          <div style={{ width: 28, height: 2, background: "#4f46e5", borderRadius: 2, margin: "10px auto 0" }} />
        </div>

        {/* Calendar */}
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Calendar selectedDate={selectedDate} setSelectedDate={setSelectedDate} orders={orders} />
        </div>

        {/* Stat cards */}
        <div className="st-grid">

          {/* ── Card 1: Ordini del giorno ── */}
          <StatCard
            accentColor="#3b82f6"
            icon={<FaCalendarDay />}
            label="Ordini del giorno"
            bigNumber={`${dayEarnings.toFixed(2)} €`}
            bigNumberColor="#1e40af"
            sub={dayjs(selectedDate).format("DD MMMM YYYY")}
          >
            {(show) => (
              <>
                <div className="st-section-label">Dettaglio clienti</div>
                {Object.entries(dailyClientTotals).length === 0 ? (
                  <p style={{ fontSize: 12, color: "#9ca3af" }}>Nessun ordine per questo giorno.</p>
                ) : (
                  Object.entries(dailyClientTotals).map(([client, total]) => (
                    <div key={client} className="st-client-row">
                      <span style={{ fontWeight: 500, color: "#111827", textTransform: "uppercase", letterSpacing: "0.04em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                        {client}
                      </span>
                      <span style={{ fontWeight: 500, color: "#1e40af", flexShrink: 0, ...blurStyle(show) }}>
                        {total.toFixed(2)} €
                      </span>
                    </div>
                  ))
                )}
              </>
            )}
          </StatCard>

          {/* ── Card 2: Totale ordini mese ── */}
          <StatCard
            accentColor="#22c55e"
            icon={<FaClipboardList />}
            label="Totale ordini mese"
            bigNumber={`${totalEarnings.toFixed(2)} €`}
            bigNumberColor="#166534"
            sub={currentMonthName}
          >
            {(show) => (
              <>
                <div className="st-section-label">Saldi clienti</div>
                {clientEntries.length === 0 ? (
                  <p style={{ fontSize: 12, color: "#9ca3af" }}>Nessun dato per questo mese.</p>
                ) : (
                  <>
                    {clientEntries.map(([client, totals], idx) => (
                      <ClientBlock
                        key={client}
                        client={client}
                        totals={totals}
                        debt={clientDebts[client]}
                        isLast={idx === clientEntries.length - 1}
                        show={show}
                        onDownloadPdf={handleDownloadPdf}
                      />
                    ))}
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      marginTop: 14, padding: "10px 12px", borderRadius: 10,
                      background: "#f5f3ff", border: "1px solid #ddd6fe",
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#4f46e5" }}>Totale mese</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#4f46e5", ...blurStyle(show) }}>
                        {grandTotalDovuto.toFixed(2)} €
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          </StatCard>

        </div>
      </div>
    </div>
  );
};

export default Statistics;